import React from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';

import withStyles from '@mui/styles/withStyles';

import { linkAnchorColor } from '../md/assets/jss/material-dashboard-pro-react.jsx';

import { createStdRequest, apiFetch, validateAcceptablePassword, showAlert } from '../utils/utils';

import { extractInitialsFromFullName } from '../utils/activityProcessor';

import DA from '../store/ReduxAccess';
import { updatePerson } from '../store/slices/personsSlice';
import { setUserInfo } from '../store/slices/userInfoSlice';

import CrmButton from './CrmButton';
import CrmTextField from './CrmTextField';
import AlertModal from './AlertModal';
import PhoneInput from './PhoneInput';

const userProfilePanelStyles = {
  titleBoxContainer: {},
  titleBox: {
    height: '58px',
    width: '395px',
    display: 'flex',
    alignItems: 'center',
  },
  titleIcon: {
    height: '50px',
    width: '50px',
    margin: '0 10px 0 15px',
    position: 'relative',
    display: 'flex',
  },
  titleBox_text: {
    display: 'flex',
    flexFlow: 'column nowrap',
    width: 'calc(100% - 75px)',
    '&:after': {
      content: '',
      clear: 'both',
    },
  },
  title: {
    color: '#0c0d0d',
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: '28px',
    cursor: 'pointer',
    maxWidth: '70%',
    '&:hover': {
      color: '#0c0dee',
    },
  },
  subtitle: {
    color: '#0c0d0d',
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: '19px',
  },
  emailDiv: {
    marginTop: '12px',
  },
  emailSpan: {
    fontWeight: 600,
  },
  profileImageComponent: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    textAlign: 'center',
    backgroundColor: '#dce3e6',
    color: 'rgba(12,13,13,0.32)',
    position: 'relative',
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: '50px',
  },
  profileImageComponent_circle: {
    verticalAlign: 'center',
    borderRadius: '50%',
  },
  changePasswordLinkDiv: {
    marginTop: '16px',
  },
  changePasswordLinkSpan: {
    color: linkAnchorColor,
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  changePasswordForm: {
    border: '1px solid black',
  },
  hidePasswordForm: {
    display: 'none',
  },
  textField: {
    margin: '10px 10px 0',
    width: 240,
  },
  passwordButtonsDiv: {
    textAlign: 'right',
    padding: '12px 8px 8px 0',
  },
  uploadPhotoDiv: {
    marginTop: '16px',
  },
  u_ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

/*
Call this with
    <UserProfilePanel
        userEmail={User's email address}
    />
*/

let setShowEditProfileModal;

class UserProfilePanel extends React.Component {
  state = {
    showPasswordForm: false,
    newPassword: '',
    confirmPassword: '',
  };

  togglePasswordUiVisibility() {
    this.setState({ showPasswordForm: !this.state.showPasswordForm });
  }

  hidePasswordUi() {
    this.setState({ showPasswordForm: false });
  }

  handlePasswordChange(event, stateKey) {
    const newValue = event.target.value.trim();
    this.setState({ [stateKey]: newValue });
  }

  validateFormAndSubmitPasswordChange() {
    const validation = this.validateForm();
    if (validation.valid) {
      const uri = '/api/account/resetPassword';
      const apiStruct = {
        email: this.props.userEmail,
        newPassword: this.state.newPassword,
      };
      const request = createStdRequest(uri, 'post', apiStruct);
      const actionOnGoodResponse = (json) => {
        if (json.success) {
          this.hidePasswordUi();
          this.clearForm();
        } else {
          showAlert(this, json.errorMessage);
        }
      };
      apiFetch(this, request, actionOnGoodResponse);
    } else {
      showAlert(this, validation.errorMessage);
    }
  }

  validateForm() {
    const validation = validateAcceptablePassword(this.state.newPassword);
    if (!validation.valid) {
      return validation;
    }
    if (this.state.confirmPassword !== this.state.newPassword) {
      return {
        valid: false,
        errorMessage: "Your New Password and your ConfirmPassword don't match",
      };
    }
    return { valid: true };
  }

  validateAndSaveEditedValues(values) {
    const validation = this.validateEditableValues(values);
    if (validation.valid) {
      const { first, middles, last, pref, phone } = values;
      const uri = '/api/account/modifyEditableProfileValues';
      const apiStruct = {
        email: this.props.userEmail,
        firstName: first,
        middleNames: middles,
        lastName: last,
        preferredName: pref,
        phoneNumber: phone,
      };
      const request = createStdRequest(uri, 'post', apiStruct);
      const actionOnGoodResponse = (json) => {
        if (json.success) {
          setShowEditProfileModal(false);
          const user = DA.getUserByEmail(this.props.userEmail);
          const personBefore = DA.getPersonById(user.personId);
          const nameToUse = pref || first;
          const personAfter = {
            ...personBefore,
            firstName: first,
            middleNames: middles,
            lastName: last,
            preferredName: pref,
            preferredFullName: `${nameToUse} ${last}`,
          };
          const resolvedPersonAfter = DA.resolvePersonDatesToIso(personAfter);
          this.props.updatePerson(resolvedPersonAfter);
          const userInfoBefore = this.props.userInfo;
          const userInfoAfter = {
            ...userInfoBefore,
            prefName: pref,
            lastName: last,
            phone: phone,
          };
          this.props.setUserInfo(userInfoAfter);
          this.setState({ showPasswordForm: false });
        } else {
          showAlert(this, json.errorMessage);
        }
      };
      apiFetch(this, request, actionOnGoodResponse);
    } else {
      showAlert(this, validation.errorMessage);
    }
  }

  validateEditableValues(values) {
    const { first, last, phone } = values;
    if (!first) {
      return {
        valid: false,
        errorMessage: 'You must provide a non-empty First Name',
      };
    }
    if (!last) {
      return {
        valid: false,
        errorMessage: 'You must provide a non-empty Last Name',
      };
    }
    if (phone && !this.isValidPhone(phone)) {
      return {
        valid: false,
        errorMessage: "If you provide a Phone Number, it must be in the form '(AAA) EEE-NNNN'",
      };
    }
    return { valid: true };
  }

  phoneRegEx = /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/;

  isValidPhone(phoneNumber) {
    return this.phoneRegEx.test(phoneNumber);
  }

  clearForm() {
    this.freshenState();
  }

  freshenState() {
    const freshState = {
      showPasswordForm: false,
      newPassword: '',
      confirmPassword: '',
    };
    this.setState(freshState);
  }

  render() {
    const { classes, userEmail, userInfo } = this.props;
    const user = DA.getUserByEmail(userEmail);
    const person = DA.getPersonById(user.personId);
    const fullName = person.preferredFullName;
    const initials = extractInitialsFromFullName(fullName);
    const userCategory = this.props.userInfo.role;
    const classForPasswordForm = this.state.showPasswordForm ? classes.changePasswordForm : classes.hidePasswordForm;
    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <EditProfileModal
          initialFirstName={person.firstName}
          initialMiddleNames={person.middleNames}
          initialLastName={person.lastName}
          initialPreferredName={person.preferredName}
          initialPhoneNumber={userInfo.phone}
          onSave={(values) => this.validateAndSaveEditedValues(values)}
        />

        <div className={classes.titleBoxContainer}>
          <div className={classes.titleBox}>
            <div className={classes.titleIcon}>
              <div className={cx(classes.profileImageComponent, classes.profileImageComponent_circle)}>{initials}</div>
            </div>
            <div className={classes.titleBox_text}>
              <div className={cx(classes.title, classes.u_ellipsis)}>{fullName}</div>
              <div className={cx(classes.subtitle, classes.u_ellipsis)}>User Category: {userCategory}</div>
            </div>
          </div>
          <div className={cx(classes.subtitle, classes.emailDiv, classes.u_ellipsis)}>
            <span className={classes.emailSpan}>Email:</span> {userEmail}
          </div>
          <div className={cx(classes.subtitle, classes.emailDiv, classes.u_ellipsis)}>
            <span className={classes.emailSpan}>Mobile Phone:</span> {userInfo.phone}
          </div>
          <div className={classes.changePasswordLinkDiv}>
            <CrmButton filled onClick={() => setShowEditProfileModal(true)}>
              Edit
            </CrmButton>
            <CrmButton outlined onClick={() => this.togglePasswordUiVisibility()}>
              Change Password
            </CrmButton>
          </div>
          <div className={classForPasswordForm}>
            <div>
              <CrmTextField
                className={classes.textField}
                label="New Password"
                password
                value={this.state.newPassword}
                onChange={(event) => this.handlePasswordChange(event, 'newPassword')}
              />
              <CrmTextField
                className={classes.textField}
                label="Confirm New Password"
                password
                value={this.state.confirmPassword}
                onChange={(event) => this.handlePasswordChange(event, 'confirmPassword')}
              />
              <div className={classes.passwordButtonsDiv}>
                <CrmButton outlined onClick={() => this.hidePasswordUi()}>
                  Cancel
                </CrmButton>
                <CrmButton filled onClick={() => this.validateFormAndSubmitPasswordChange()}>
                  Submit
                </CrmButton>
              </div>
              <div />
            </div>
          </div>
          {/* <div className={classes.uploadPhotoDiv}>
            Upload Photo
          </div>*/}
        </div>
      </div>
    );
  }
}

const EditProfileModal = ({
  initialFirstName,
  initialMiddleNames,
  initialLastName,
  initialPreferredName,
  initialPhoneNumber,
  onSave,
}) => {
  const [open, setOpen] = React.useState(false);
  const [firstName, setFirstName] = React.useState(initialFirstName);
  const [middleNames, setMiddleNames] = React.useState(initialMiddleNames);
  const [lastName, setLastName] = React.useState(initialLastName);
  const [preferredName, setPreferredName] = React.useState(initialPreferredName);
  const [phoneNumber, setPhoneNumber] = React.useState(initialPhoneNumber);

  const handleClose = () => {
    setOpen(false);
  };

  setShowEditProfileModal = setOpen;

  const packageAndSubmitValues = () => {
    const values = {
      first: firstName.trim(),
      middles: middleNames.trim(),
      last: lastName.trim(),
      pref: preferredName.trim(),
      phone: phoneNumber.trim(),
    };
    onSave(values);
  };

  const handlePhoneRolesChange = () => {};

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Edit Profile</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <div>
              <CrmTextField
                label="First Name"
                id="firstName"
                required
                fullWidth
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </div>
            <div>
              <CrmTextField
                label="Middle Name(s)"
                id="middleNames"
                fullWidth
                value={middleNames}
                onChange={(event) => setMiddleNames(event.target.value)}
              />
            </div>
            <div>
              <CrmTextField
                label="Last Name"
                id="lastName"
                required
                fullWidth
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div>
              <CrmTextField
                label="Preferred Name"
                id="preferredName"
                fullWidth
                value={preferredName}
                onChange={(event) => setPreferredName(event.target.value)}
              />
            </div>
            <div>
              <PhoneInput
                possibleRoles={['Mobile']}
                actualRoles={['Mobile']}
                number={phoneNumber}
                onRolesChange={(rolesObj) => handlePhoneRolesChange(rolesObj)}
                onNumberChange={(newNumber) => setPhoneNumber(event.target.value)}
              />
            </div>
            <div style={{ paddingLeft: '12px', paddingTop: '14px' }}>
              <CrmButton filled onClick={() => packageAndSubmitValues()}>
                Save
              </CrmButton>
              <CrmButton outlined onClick={() => setOpen(false)}>
                Cancel
              </CrmButton>
            </div>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

const actionProducers = {
  updatePerson,
  setUserInfo,
};

export default connect(null, actionProducers)(withStyles(userProfilePanelStyles)(UserProfilePanel));
